import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { localizedName } from '@/lib/localized';
import type { CatalogItem } from '../lib';
import { Callout } from './controls';
import {
  type EditWarning,
  type ItemDiff,
  type MenuChange,
  type MenuDraft,
  type MenuSnapshot,
  type StepId,
  editWarnings,
  itemBatches,
  menuChanges,
  publishVerdict,
  updateMenuBody,
} from './wizardState';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/**
 * Step 4 — the one screen in this group with no existing equivalent.
 *
 * Its only job is to be checkable, so every value is read back from the draft
 * rather than from what the operator remembers typing, and each section links
 * to the step that owns it.
 *
 * The banner is the part that had to change from the frame. The design states
 * flatly that the menu "will be visible to guests immediately after creation";
 * `resolveActiveMenu` returns exactly one menu per guest request, ordered
 * `isDefault desc, createdAt asc`, so that sentence is false for an inactive
 * menu, false for one with no items, and false for a non-default menu created
 * after an existing one. It reports which of those is true instead.
 *
 * ## Reviewing a change rather than a creation
 *
 * With a `before` snapshot the step grows two blocks the create path does not
 * have, and "Ready to publish" stops being the sentence at the bottom:
 *
 * - **What will change** — every field whose value differs, old struck through
 *   beside new. The item half now goes through `POST`/`DELETE
 *   /menus/:id/items/bulk`, chunked at `ITEMS_BULK_MAX`, and each chunk is one
 *   transaction — so a save with everything in one chunk cannot partly land.
 *   The request count only earns a line here when it is genuinely more than
 *   one call, because that is the one case where an earlier call landing and a
 *   later one failing is still possible.
 * - **What it takes away** — removals, deactivation, losing or seizing the
 *   default flag, giving up the tenant-wide fallback, and closing an always-open
 *   menu. Adding to a draft is safe; none of these are.
 *
 * The verdict underneath is the same function either way, because the question
 * it answers — will a guest reach this menu once this is saved — does not change
 * with the mode. Only the wording of its happy case does.
 */
export function ReviewStep({
  draft,
  items,
  before,
  diff,
  branchLabel,
  otherMenusExist,
  formatMoney,
  onEdit,
}: {
  draft: MenuDraft;
  items: CatalogItem[];
  /** The menu as the server holds it. Absent on a create. */
  before?: MenuSnapshot;
  diff?: ItemDiff;
  branchLabel: (branchId: string | null) => string;
  otherMenusExist: boolean;
  formatMoney: (amount: string) => string;
  onEdit: (step: StepId) => void;
}) {
  const { t, i18n } = useTranslation();
  const byId = new Map(items.map((item) => [item.id, item]));
  const verdict = publishVerdict(draft, otherMenusExist);
  const changes = before && diff ? menuChanges(before, draft, diff) : null;
  const warnings = before && diff ? editWarnings(before, draft, diff) : [];
  /**
   * How many HTTP calls the item half of the save actually costs, plus one
   * more if any non-item field changed. Below 2 the whole save is a single
   * request — one transaction, all or nothing — so there is nothing honest to
   * warn about; the multi-request line only earns its place once landing part
   * of the save and losing the rest is a real possibility again.
   */
  const requestCount =
    diff && changes
      ? itemBatches(diff).length + (changes.some((change) => change !== 'items') ? 1 : 0)
      : 0;

  /** One consequence, in the guest's terms. */
  const warningText = (warning: EditWarning) => {
    const count = diff?.detach.length ?? 0;
    switch (warning) {
      case 'emptyingMenu':
        return t('catalog.menus.wizard.edit.warn.emptyingMenu', { count });
      case 'removingItems':
        return t('catalog.menus.wizard.edit.warn.removingItems', { count });
      case 'deactivating':
        return t('catalog.menus.wizard.edit.warn.deactivating');
      case 'losingDefault':
        return t('catalog.menus.wizard.edit.warn.losingDefault');
      case 'becomingDefault':
        return t('catalog.menus.wizard.edit.warn.becomingDefault');
      case 'losingTenantFallback':
        return t('catalog.menus.wizard.edit.warn.losingTenantFallback');
      case 'narrowingSchedule':
        return t('catalog.menus.wizard.edit.warn.narrowingSchedule');
    }
  };

  const scheduleSummary = (windows: number) =>
    windows === 0
      ? t('catalog.menus.wizard.alwaysAvailable')
      : t('catalog.menus.wizard.edit.windowCount', { count: windows });

  /** The two sides of one changed field, as text, plus anything the two sides
   *  cannot say on their own. */
  const sides: Record<MenuChange, () => { was: string; now: string; detail?: string }> = {
    name: () => ({ was: before!.name, now: draft.name.trim() }),
    branch: () => ({
      was: branchLabel(before!.branchId),
      now: branchLabel(updateMenuBody(draft).branchId),
    }),
    isDefault: () => ({
      was: t(before!.isDefault ? 'common.yes' : 'common.no'),
      now: t(draft.isDefault ? 'common.yes' : 'common.no'),
    }),
    isActive: () => ({
      was: t(before!.isActive ? 'catalog.items.active' : 'catalog.items.inactive'),
      now: t(draft.isActive ? 'catalog.items.active' : 'catalog.items.inactive'),
    }),
    schedule: () => ({
      was: scheduleSummary(before!.schedule.length),
      now: scheduleSummary(updateMenuBody(draft).schedule.length),
    }),
    items: () => ({
      // Symmetric counts on the two sides; the breakdown is a third thing and
      // reads as nonsense struck through against a count.
      was: t('catalog.items.bulk.selected', { count: before!.items.length }),
      now: t('catalog.items.bulk.selected', { count: draft.items.length }),
      detail: t('catalog.menus.wizard.edit.itemsChanged', { added: diff!.attach.length, removed: diff!.detach.length, moved: diff!.update.length }),
    }),
  };

  const CHANGE_LABEL: Record<MenuChange, string> = {
    name: t('catalog.items.name'),
    branch: t('catalog.menus.branch'),
    isDefault: t('catalog.menus.isDefault'),
    isActive: t('common.status'),
    schedule: t('catalog.menus.schedule'),
    items: t('catalog.menus.items'),
  };

  const stepOf: Record<MenuChange, StepId> = {
    name: 'basic',
    branch: 'basic',
    isDefault: 'basic',
    isActive: 'basic',
    schedule: 'schedule',
    items: 'items',
  };

  return (
    <div className="max-w-3xl space-y-5">
      {changes && (
        <Card>
          <CardContent className="space-y-3 p-5">
            <h3 className="text-sm font-semibold">
              {t('catalog.menus.wizard.edit.summaryTitle')}
            </h3>

            {changes.length === 0 ? (
              // `dir="auto"` on every sentence that carries a name or a
              // number from the venue: whichever way the text runs, the
              // punctuation lands on the end the sentence actually finishes at.
              <p dir="auto" className="rounded-lg border p-3 text-sm text-ink-muted">
                {t('catalog.menus.wizard.edit.noChangesBody')}
              </p>
            ) : (
              <>
                <dl className="divide-y rounded-lg border">
                  {changes.map((change) => {
                    const { was, now, detail } = sides[change]();
                    return (
                      <div
                        key={change}
                        className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 p-3 text-sm"
                      >
                        <dt className="text-ink-muted">
                          <button
                            type="button"
                            onClick={() => onEdit(stepOf[change])}
                            className="rounded underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {CHANGE_LABEL[change]}
                          </button>
                        </dt>
                        {/* No arrow glyph between the two: a `→` points the
                            wrong way in Arabic, and struck-through-then-plain
                            reads the same in both directions. */}
                        <dd className="min-w-0">
                          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span dir="auto" className="text-ink-muted line-through">
                              {was}
                            </span>
                            <span dir="auto" className="font-medium">
                              {now}
                            </span>
                          </span>
                          {detail && (
                            <span dir="auto" className="block text-xs text-ink-muted">
                              {detail}
                            </span>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </dl>

                {/* Only earns a line when it is genuinely more than one
                    request — a single bulk call cannot partly land, so
                    warning about it would be dishonest in the common case. */}
                {requestCount > 1 && (
                  <p dir="auto" className="text-xs text-ink-muted">
                    {t('catalog.menus.wizard.edit.requests', { count: requestCount })}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {warnings.length > 0 && (
        <Callout
          tone="warning"
          title={t('catalog.menus.wizard.edit.warnTitle')}
          body={
            <ul className="mt-1 space-y-1">
              {warnings.map((warning) => (
                <li key={warning} dir="auto">
                  {warningText(warning)}
                </li>
              ))}
            </ul>
          }
        />
      )}

      <Card>
        <CardContent className="space-y-6 p-5">
          <Section title={t('catalog.menus.wizard.step.basicInfo')} onEdit={() => onEdit('basic')}>
            <dl className="divide-y rounded-lg border">
              <Row label={t('catalog.items.name')}>
                <span dir="auto">{draft.name}</span>
              </Row>
              <Row label={t('catalog.menus.wizard.menuType')}>
                {t(draft.scope === 'branch' ? 'catalog.menus.branchScoped' : 'catalog.menus.tenantWide')}
              </Row>
              {draft.scope === 'branch' && (
                <Row label={t('catalog.menus.branch')}>{branchLabel(draft.branchId || null)}</Row>
              )}
              <Row label={t('catalog.menus.isDefault')}>{t(draft.isDefault ? 'common.yes' : 'common.no')}</Row>
              <Row label={t('common.status')}>
                <Badge tone={draft.isActive ? 'success' : 'neutral'}>
                  {t(draft.isActive ? 'catalog.items.active' : 'catalog.items.inactive')}
                </Badge>
              </Row>
            </dl>
          </Section>

          <Section title={t('catalog.menus.schedule')} onEdit={() => onEdit('schedule')}>
            {draft.alwaysAvailable || draft.schedule.length === 0 ? (
              <p className="rounded-lg border p-3 text-sm">{t('catalog.menus.alwaysOpen')}</p>
            ) : (
              <ul className="divide-y rounded-lg border text-sm">
                {draft.schedule.map((window, index) => (
                  <li key={index} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-3">
                    <span>
                      {window.days
                        .map((day) => t(`catalog.menus.day.${DAY_KEYS[day]}`))
                        .join(t('catalog.items.bulk.listSeparator'))}
                    </span>
                    <span className="text-ink-muted">
                      {t('catalog.menus.from')} {window.from} · {t('catalog.menus.to')} {window.to}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            title={`${t('catalog.menus.items')} · ${t('catalog.items.bulk.selected', { count: draft.items.length })}`}
            onEdit={() => onEdit('items')}
          >
            {draft.items.length === 0 ? (
              <p className="rounded-lg border p-3 text-sm text-ink-muted">
                {t('catalog.menus.noItems')}
              </p>
            ) : (
              <ul className="divide-y rounded-lg border text-sm">
                {draft.items.map((row) => {
                  const item = byId.get(row.itemId);
                  return (
                    <li key={row.itemId} className="flex items-center gap-2 p-3">
                      <span dir="auto" className="min-w-0 flex-1 truncate">
                        {item ? localizedName(item, i18n.language) : row.itemId}
                      </span>
                      {!row.isVisible && <Badge tone="neutral">{t('catalog.menus.hidden')}</Badge>}
                      <span className="shrink-0 tabular-nums">
                        {formatMoney(item?.basePrice ?? '0')}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
        </CardContent>
      </Card>

      {verdict === 'ready' ? (
        <Callout
          tone="success"
          title={
            before
              ? t('catalog.menus.wizard.edit.readyTitle')
              : t('catalog.menus.wizard.readyTitle')
          }
          body={
            before
              ? t('catalog.menus.wizard.edit.readyBody')
              : t('catalog.menus.wizard.readyBody')
          }
        />
      ) : (
        <Callout
          tone="warning"
          title={t('catalog.menus.wizard.notReadyTitle')}
          body={t(`catalog.menus.wizard.notReady.${verdict}`)}
        />
      )}
    </div>
  );
}

function Section({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <button
          type="button"
          onClick={onEdit}
          className="shrink-0 rounded text-sm text-accent underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('common.edit')}
        </button>
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 p-3 text-sm">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="min-w-0 font-medium">{children}</dd>
    </div>
  );
}
